import React, { useState, useRef, useEffect } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
  Calendar, History, Star, Wallet, FileText,
} from "lucide-react";
import { ChevronLeft, ChevronRight } from "@mui/icons-material";
import { RootState } from "../../../../redux/store";

import ShortAccomodationBookings  from "./ShortAccomodationBookings";
import PastShortAccomodations     from "./PastShortAccomodations";

import ShortGuestCredits          from "./ShortGuestCredits";

// 👇 confirm this path matches your real logo asset
import logoImg from "../../../../assets/app/logo.png";
import GuestShortRequests from "./GuestShortRequest";
import Review from "./Review";

/* ── brand gradient — the ONLY accent color used anywhere in this file ── */
const GRADIENT = "linear-gradient(125deg,#0a0a0a,#bf9b30,#d46b10,#e84020,#bf9b30,#0a0a0a)";

const T = {
  bg:        "#F2F1EC",
  surface:   "#ffffff",
  border:    "#e8e7e2",
  text:      "#0a0a0a",
  textSoft:  "#6b6a65",
  muted:     "#9a9a94",
  sans:  "'Montserrat', sans-serif",
  serif: "'Cormorant Garamond', serif",
};

const FONT_CSS = `
  @import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:wght@300;400;500&family=Montserrat:wght@300;400;500;600&display=swap');
  * { box-sizing: border-box; }

  @keyframes aeSpin { 0%{background-position:0% 50%} 50%{background-position:100% 50%} 100%{background-position:0% 50%} }
  @keyframes aeBreathe { 0%,100%{transform:scale(1)} 50%{transform:scale(1.06)} }

  .desk-sidebar { display: flex !important; }
  .mob-tabs-bar { display: none; }

  @media (max-width: 767px) {
    .mob-tabs-bar { display: block; }
    .desk-sidebar { display: none !important; }
    .desk-main    { margin-left: 0 !important; }
    .desk-crumb   { display: none !important; }
    .mob-crumb    { display: flex !important; }
  }

  .tabs-scroll {
    display: flex;
    gap: 4px;
    overflow-x: auto;
    padding: 10px 14px;
    scrollbar-width: none;
    -webkit-overflow-scrolling: touch;
  }
  .tabs-scroll::-webkit-scrollbar { display: none; }

  /* mobile tab — NO card, NO border, NO background box */
  .tab-pill {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 4px;
    padding: 4px 12px;
    background: transparent;
    border: none;
    cursor: pointer;
    flex-shrink: 0;
    min-width: 58px;
    font-family: 'Montserrat', sans-serif;
  }
  .tab-pill-label {
    font-size: 10px; font-weight: 500; color: #9a9a94;
    white-space: nowrap; letter-spacing: .02em; transition: color .15s, font-weight .15s;
  }
  .tab-pill.active .tab-pill-label { color: #0a0a0a; font-weight: 700; }

  .mob-crumb { display: none; align-items: center; gap: 6px; height: 34px; padding: 0 16px; background: #ffffff; border-bottom: 0.5px solid #e8e7e2; }

  @keyframes fadeUp { from{opacity:0;transform:translateY(8px)} to{opacity:1;transform:translateY(0)} }
  .fade-up { animation: fadeUp .22s ease both; }

  .ae-press:active { transform: translateY(1px) scale(.98) !important; }
`;

const HEADER_H     = 56;
const SIDEBAR_FULL = 220;
const SIDEBAR_MINI = 56;

type TabKey = "current" | "requests" | "past" | "reviews" | "credits";
type Tab = { group: string; title: string; key: TabKey; icon: React.ReactNode };

const TABS: Tab[] = [
  { group: "Bookings", title: "Current",  key: "current",  icon: <Calendar  size={16} /> },
  { group: "Bookings", title: "Requests", key: "requests", icon: <FileText  size={16} /> },
  { group: "Bookings", title: "Past",     key: "past",     icon: <History   size={16} /> },
  { group: "Account",  title: "Reviews",  key: "reviews",  icon: <Star      size={16} /> },
  { group: "Account",  title: "Credits",  key: "credits",  icon: <Wallet    size={16} /> },
];

/* ── BrandRing: squircle frame with a THICK animated gradient ring so every
   color in the gradient (black/gold/orange/red) actually reads as a band,
   not a blurred sliver. Used for logo, avatars, and active icon badges. ── */
const BrandRing: React.FC<{
  size: number;
  radius?: number;
  thickness?: number;
  spin?: boolean;
  breathe?: boolean;
  children: React.ReactNode;
}> = ({ size, radius = 10, thickness = 3, spin = true, breathe = false, children }) => (
  <div
    style={{
      width: size,
      height: size,
      borderRadius: radius,
      flexShrink: 0,
      background: GRADIENT,
      backgroundSize: spin ? "400% 100%" : undefined,
      animation: [
        spin ? "aeSpin 4s linear infinite" : "",
        breathe ? "aeBreathe 1.8s ease-in-out infinite" : "",
      ].filter(Boolean).join(", ") || undefined,
      padding: thickness,
      boxShadow: "0 3px 10px rgba(0,0,0,.16)",
    }}
  >
    <div
      style={{
        width: "100%", height: "100%",
        borderRadius: Math.max(radius - thickness, 5),
        background: "#fff",
        overflow: "hidden",
        display: "flex", alignItems: "center", justifyContent: "center",
      }}
    >
      {children}
    </div>
  </div>
);

/* plain (inactive) icon — no badge, no box */
const PlainIcon: React.FC<{ children: React.ReactNode; size?: number }> = ({ children, size = 28 }) => (
  <span style={{
    width: size, height: size, display: "flex", alignItems: "center", justifyContent: "center",
    color: "#9a9a94", flexShrink: 0,
  }}>
    {children}
  </span>
);

/* ════════════════════════════════════════════════════════════ */
const ShortBookingsDashboard: React.FC = () => {
  const navigate     = useNavigate();
  const user         = useSelector((s: RootState) => s.auth.user);
  const userId       = user?.userId;

  const [active,    setActive]    = useState<TabKey>("current");
  const [collapsed, setCollapsed] = useState(false);
  const activeTabRef = useRef<HTMLButtonElement>(null);

  const groups      = Array.from(new Set(TABS.map(t => t.group)));
  const sbW         = collapsed ? SIDEBAR_MINI : SIDEBAR_FULL;
  const activeTitle = TABS.find(t => t.key === active)?.title ?? "";

  useEffect(() => {
    activeTabRef.current?.scrollIntoView({ behavior: "smooth", inline: "center", block: "nearest" });
  }, [active]);

  const renderContent = () => {
    switch (active) {
      case "current":  return <ShortAccomodationBookings />;
      case "requests": return <GuestShortRequests />;
      case "past":     return <PastShortAccomodations />;
      case "reviews":  return <Review />;
      case "credits":  return <ShortGuestCredits />;
      default:         return <ShortAccomodationBookings />;
    }
  };

  /* ── shared avatar with logo fallback ── */
  const Avatar = ({ size, ringThickness }: { size: number; ringThickness: number }) => {
    const [src, setSrc] = useState(user?.profilePicture || logoImg);
    useEffect(() => { setSrc(user?.profilePicture || logoImg); }, [user?.profilePicture]);
    return (
      <BrandRing size={size} radius={Math.round(size * 0.32)} thickness={ringThickness} spin={false}>
        <img
          src={src}
          alt=""
          onError={() => setSrc(logoImg)}
          style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
        />
      </BrandRing>
    );
  };

  /* ── desktop sidebar nav row — flat, no card, no border, no box-shadow ── */
  const NavItem = ({ tab }: { tab: Tab }) => {
    const isActive = active === tab.key;
    return (
      <button
        onClick={() => setActive(tab.key)}
        title={collapsed ? tab.title : undefined}
        className="ae-press"
        style={{
          display: "flex", alignItems: "center",
          gap: collapsed ? 0 : 12,
          justifyContent: collapsed ? "center" : "flex-start",
          width: "100%",
          padding: collapsed ? "10px 0" : "8px 10px",
          borderRadius: 10, border: "none", cursor: "pointer",
          background: isActive ? T.bg : "transparent",
          fontFamily: T.sans, marginBottom: 4,
          transition: "background .15s, transform .1s",
          color: isActive ? T.text : T.muted,
        }}
        onMouseEnter={e => { if (!isActive) (e.currentTarget as HTMLElement).style.background = "#f7f6f1"; }}
        onMouseLeave={e => { if (!isActive) (e.currentTarget as HTMLElement).style.background = "transparent"; }}
      >
        {isActive ? (
          <BrandRing size={28} radius={9} thickness={2} breathe>
            <span style={{ color: "#0a0a0a", display: "flex" }}>{tab.icon}</span>
          </BrandRing>
        ) : (
          <PlainIcon size={28}>{tab.icon}</PlainIcon>
        )}

        {!collapsed && (
          <span style={{ fontSize: 12, flex: 1, whiteSpace: "nowrap", fontWeight: isActive ? 700 : 400, textAlign: "left" }}>
            {tab.title}
          </span>
        )}
      </button>
    );
  };

  /* ════════════════════════════════════════════════════════════ */
  return (
    <div style={{ display: "flex", flexDirection: "column", minHeight: "100vh", background: T.bg, fontFamily: T.sans, color: T.text }}>
      <style>{FONT_CSS}</style>

      {/* ── HEADER ── */}
      <header style={{
        position: "fixed", top: 0, left: 0, right: 0, height: HEADER_H,
        background: "#ffffff",
        display: "flex", alignItems: "center", justifyContent: "space-between",
        padding: "0 16px", zIndex: 300,
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <BrandRing size={30} radius={10} thickness={2.5}>
            <img src={logoImg} alt="Areluxe" style={{ height: "100%", width: "100%", objectFit: "cover", display: "block" }} />
          </BrandRing>
          <span style={{ color: T.text, fontSize: 13, fontWeight: 400, letterSpacing: "0.3em", fontFamily: T.serif }}>
            ARELUXE
          </span>
        </div>

        <div onClick={() => navigate(`/profile/${userId}`)} style={{ cursor: "pointer" }} title="View profile">
          <Avatar size={32} ringThickness={2.5} />
        </div>

        {/* animated gradient hairline — this is where red/orange/gold all read clearly */}
        <div style={{
          position: "absolute", left: 0, right: 0, bottom: 0, height: 3,
          background: GRADIENT, backgroundSize: "400% 100%", animation: "aeSpin 5s linear infinite",
        }} />
      </header>

      {/* ── MOBILE TABS BAR ── */}
      <div
        className="mob-tabs-bar"
        style={{
          position: "fixed", top: HEADER_H, left: 0, right: 0,
          background: T.surface, borderBottom: `1px solid ${T.border}`, zIndex: 200,
        }}
      >
        <div className="tabs-scroll">
          {TABS.map(({ key, title, icon }) => {
            const isActive = active === key;
            return (
              <button
                key={key}
                ref={isActive ? activeTabRef : null}
                className={`tab-pill${isActive ? " active" : ""}`}
                onClick={() => setActive(key as TabKey)}
              >
                {isActive ? (
                  <BrandRing size={30} radius={10} thickness={2.5} breathe>
                    <span style={{ color: "#0a0a0a", display: "flex" }}>{icon}</span>
                  </BrandRing>
                ) : (
                  <PlainIcon size={30}>{icon}</PlainIcon>
                )}
                <span className="tab-pill-label">{title}</span>
              </button>
            );
          })}
        </div>
      </div>

      {/* ── BODY ── */}
      <div style={{ display: "flex", paddingTop: HEADER_H, flex: 1 }}>

        {/* ── DESKTOP SIDEBAR ── */}
        <aside
          className="desk-sidebar"
          style={{
            position: "fixed", top: HEADER_H, left: 0, bottom: 0,
            width: sbW, background: T.surface,
            borderRight: `1px solid ${T.border}`,
            flexDirection: "column",
            transition: "width .25s ease",
            zIndex: 200, overflow: "hidden",
          }}
        >
          {!collapsed && (
            <div style={{ padding: "14px 14px", borderBottom: `1px solid ${T.border}` }}>
              <div style={{
                background: T.bg, borderRadius: 12, padding: "10px 12px",
                display: "flex", alignItems: "center", gap: 10,
              }}>
                <Avatar size={34} ringThickness={2.5} />
                <div style={{ minWidth: 0 }}>
                  <p style={{ fontSize: 11, color: T.text, fontWeight: 600, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {user?.firstName || "Guest"}
                  </p>
                  <p style={{ fontSize: 9, color: T.muted, marginTop: 1 }}>My stays</p>
                </div>
              </div>
            </div>
          )}

          <nav style={{ flex: 1, overflowY: "auto", padding: "10px 10px", scrollbarWidth: "none" as const }}>
            {groups.map(group => (
              <div key={group}>
                {!collapsed
                  ? <p style={{ fontSize: 9, color: T.muted, letterSpacing: "0.13em", textTransform: "uppercase", padding: "10px 4px 6px" }}>
                      {group}
                    </p>
                  : <div style={{ height: 10 }} />
                }
                {TABS.filter(t => t.group === group).map(tab => (
                  <NavItem key={tab.key} tab={tab} />
                ))}
              </div>
            ))}
          </nav>

          <div style={{ padding: 10 }}>
            <button
              className="ae-press"
              onClick={() => setCollapsed(p => !p)}
              style={{
                display: "flex", alignItems: "center", justifyContent: "center",
                width: "100%", padding: 8, borderRadius: 10,
                border: "none", background: "transparent", cursor: "pointer",
                gap: collapsed ? 0 : 6,
              }}
              onMouseEnter={e => (e.currentTarget as HTMLElement).style.background = T.bg}
              onMouseLeave={e => (e.currentTarget as HTMLElement).style.background = "transparent"}
            >
              {collapsed
                ? <ChevronRight style={{ fontSize: 16, color: T.muted }} />
                : <>
                    <ChevronLeft style={{ fontSize: 16, color: T.muted }} />
                    <span style={{ fontSize: 11, color: T.muted }}>Collapse</span>
                  </>
              }
            </button>
          </div>

          {/* thin vertical gradient accent on the sidebar's right edge */}
          <div style={{
            position: "absolute", top: 0, right: 0, bottom: 0, width: 3,
            background: GRADIENT, backgroundSize: "100% 400%", animation: "aeSpin 5s linear infinite",
          }} />
        </aside>

        {/* ── MAIN CONTENT ── */}
        <main
          className="desk-main"
          style={{ flex: 1, marginLeft: sbW, transition: "margin-left .25s ease", minWidth: 0 }}
        >
          <div
            className="desk-crumb"
            style={{
              height: 44, background: T.surface,
              borderBottom: `0.5px solid ${T.border}`,
              display: "flex", alignItems: "center",
              gap: 6, padding: "0 24px",
            }}
          >
            <span style={{ fontSize: 11, color: T.muted }}>My stays</span>
            <span style={{ fontSize: 11, color: T.border }}>/</span>
            <span style={{ fontSize: 11, color: T.textSoft, fontWeight: 500 }}>{activeTitle}</span>
          </div>

          <div className="mob-crumb" style={{ marginTop: 66 }}>
            <span style={{ fontSize: 11, color: T.muted }}>My stays</span>
            <span style={{ fontSize: 11, color: T.border }}>/</span>
            <span style={{ fontSize: 11, color: T.textSoft, fontWeight: 500 }}>{activeTitle}</span>
          </div>

          <div className="fade-up" key={active} style={{ padding: "20px 16px 100px" }}>
            {renderContent()}
          </div>
        </main>

      </div>
    </div>
  );
};

export default ShortBookingsDashboard;